import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, AlertCircleIcon } from 'lucide-react';
interface AudioPlayerProps {
  audioUrl: string;
  title?: string;
}
export function AudioPlayer({
  audioUrl,
  title = 'Audio Preview'
}: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      console.log('Audio loaded successfully, duration:', audio.duration);
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      setError('Failed to load audio file. Please try again.');
      setIsLoading(false);
    };
    const handleCanPlay = () => {
      console.log('Audio can play');
      setIsLoading(false);
    };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, []);
  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (isPlaying) {
        audio.pause();
        console.log('Audio paused');
      } else {
        await audio.play();
        console.log('Audio playing');
      }
      setIsPlaying(!isPlaying);
    } catch (err) {
      console.error('Play error:', err);
      setError('Unable to play audio. Please check your connection.');
    }
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const time = parseFloat(e.target.value);
    audio.currentTime = time;
    setCurrentTime(time);
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const vol = parseFloat(e.target.value);
    audio.volume = vol;
    setVolume(vol);
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  if (error) {
    return <div className="bg-red-50 border-2 border-red-700 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-8">
        <div className="flex items-center gap-3" data-id="element-9">
          <AlertCircleIcon className="w-6 h-6 text-red-700 flex-shrink-0" data-id="element-10" />
          <div data-id="element-11">
            <h3 className="text-lg font-bold text-red-900 mb-1" data-id="element-12">
              Audio Unavailable
            </h3>
            <p className="text-sm text-red-700" data-id="element-13">{error}</p>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-blue-50 border-2 border-blue-700 rounded-lg p-4 md:p-6 shadow-lg relative z-30" data-id="element-14">
      <audio ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" data-id="element-15" />
      <div className="flex flex-col gap-4" data-id="element-16">
        <div className="flex items-center justify-between" data-id="element-17">
          <h3 className="text-lg md:text-xl font-bold text-blue-900" data-id="element-18">
            {title}
          </h3>
          {isLoading && <span className="text-sm text-blue-700" data-id="element-19">Loading...</span>}
        </div>
        <div className="flex items-center gap-4" data-id="element-20">
          <button onClick={togglePlayPause} disabled={isLoading} className="bg-blue-700 hover:bg-blue-800 text-white rounded-full p-3 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-21">
            {isPlaying ? <PauseIcon className="w-6 h-6" data-id="element-22" /> : <PlayIcon className="w-6 h-6" data-id="element-23" />}
          </button>
          <div className="flex-1 flex flex-col gap-2" data-id="element-24">
            <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} disabled={isLoading} className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50" style={{
            background: `linear-gradient(to right, #1e40af 0%, #1e40af ${currentTime / duration * 100}%, #bfdbfe ${currentTime / duration * 100}%, #bfdbfe 100%)`
          }} data-id="element-25" />
            <div className="flex justify-between text-sm text-gray-600" data-id="element-26">
              <span data-id="element-27">{formatTime(currentTime)}</span>
              <span data-id="element-28">{formatTime(duration)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-29">
          <Volume2Icon className="w-5 h-5 text-blue-700" data-id="element-30" />
          <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} className="w-24 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer" data-id="element-31" />
        </div>
      </div>
    </div>;
}